import React from 'react';
import { useExternalData } from '../../contexts/external-data-context';
import { useFormFields } from '../../contexts/form-fields-context';
import { type SubmitButtonField as SubmitButtonFieldEntity } from '../../types';
import { type FieldLayoutComponent } from '../../../../field-layout';
import { useForm } from '../../../../contexts/form-context';
import { useSubmitButton } from '../../../../fields/submit-button/submit-button/use-submit-button';
import { useFieldActions } from '../../../../contexts/field-actions-context';
import { FieldPropsContext } from '../../../../contexts/field-props-context';
import { FIELD_PROPS_MAPPERS } from '../../../../mappers/public-props-map';

export interface SubmitButtonFieldProps {
  field: SubmitButtonFieldEntity;
  FieldLayout: FieldLayoutComponent;
}

export const SubmitButtonField = ({
  field,
  FieldLayout,
}: SubmitButtonFieldProps) => {
  const { submitText, nextText, previousText, text } = field.properties as any;

  const componentsById = useFormFields();
  const externalData = useExternalData();
  const form = useForm();
  const { actions } = useFieldActions();
  const {
    handleSubmitClick,
    isDisabled,
    isInProgress,
    showPreviousButton,
    showNextButton,
    showSubmitButton,
    hasSteps,
  } = useSubmitButton({
    form,
    id: field.id,
  });

  const submitButtonText: string = submitText || text || '';
  const previousButtonText: string = previousText || '';
  const nextButtonText: string = nextText || '';
  const propsMapper = FIELD_PROPS_MAPPERS[field.fieldType];

  const FieldComponent = componentsById[field.fieldType];

  if (!FieldComponent) {
    return null;
  }

  return (
    <FieldPropsContext.Provider
      value={{
        ...propsMapper({
          ...field.properties,
          id: field.id,
          externalData,
          fieldType: field.fieldType,
          hasSteps,
          isDisabled,
          isInProgress,
          showPreviousButton,
          showNextButton,
          showSubmitButton,
          submitText: submitButtonText,
          previousText: previousButtonText,
          nextText: nextButtonText,
          onSubmitClick: handleSubmitClick,
          onPreviousClick: () => actions?.setPreviousStep(),
          onNextClick: () => actions?.setNextStep(),
        }),
      }}
    >
      <FieldComponent
        {...field.properties}
        id={field.id}
        externalData={externalData}
        fieldType={field.fieldType}
        FieldLayout={FieldLayout}
        hasSteps={hasSteps}
        isDisabled={isDisabled}
        isInProgress={isInProgress}
        showPreviousButton={showPreviousButton}
        showNextButton={showNextButton}
        showSubmitButton={showSubmitButton}
        submitText={submitButtonText}
        previousText={previousButtonText}
        nextText={nextButtonText}
        onSubmitClick={handleSubmitClick}
        onPreviousClick={() => actions?.setPreviousStep()}
        onNextClick={() => actions?.setNextStep()}
      />
    </FieldPropsContext.Provider>
  );
};
